import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Phone, ClipboardList, Home as HomeIcon, Building2, MapPin, ShieldCheck, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Seo from '../components/Seo';
import { BUSINESS, SERVICE_AREAS, localBusinessNode, organizationJsonLd, breadcrumbJsonLd } from '../data/businessInfo';

// Public company facts. Keep this text identical to the static summary in public/index.html.
export const ABOUT_FACTS = {
  what: 'A Quality Handyman LLC is a locally owned handyman and home-improvement company based in Albuquerque, New Mexico. We handle residential and commercial repairs, remodeling, interior and exterior painting, stucco repair, drywall, flooring, door repair and installation, carpentry, plumbing and electrical repairs, landscaping and xeriscaping, hauling, and ongoing property maintenance.',
  who: 'We work with homeowners, landlords and rental-property owners, realtors preparing homes for sale, property managers, HOAs, and commercial clients such as retail stores, offices, restaurants and multi-unit buildings.',
  where: 'We are a service-area business: our crews travel to you and we do not operate a public storefront. We serve Albuquerque and the surrounding communities of Rio Rancho, Corrales, Bernalillo, Placitas, Los Lunas and Santa Fe.',
  how: 'Request an estimate three ways: use the Instant Project Estimate tool for a labor-based price range in minutes, book an on-site visit through the online booking calendar, or call or text (505) 219-7816. Estimates are free and there is no obligation.',
  insurance: 'A Quality Handyman LLC carries business insurance. Insurance documentation can be provided on request for residential, commercial and property-management clients before work begins.',
};

const SERVICE_LINKS = [
  ['General Repairs', '/general-repairs'], ['Interior Painting', '/interior-painting'], ['Exterior Painting', '/exterior-painting'],
  ['Stucco Repair', '/stucco'], ['Drywall', '/drywall'], ['Flooring', '/flooring'], ['Door Repair & Installation', '/door-replacement'],
  ['Bathroom Remodeling', '/bathroom-remodeling'], ['Carpentry', '/carpentry'], ['Plumbing Repairs', '/plumbing'], ['Electrical Repairs', '/electrical'],
  ['Landscaping & Xeriscaping', '/landscaping'], ['Hauling & Material Delivery', '/hauling'], ['Commercial Property Maintenance', '/commercial-property-maintenance'],
  ['Rental Property Maintenance', '/rental-property-maintenance'], ['Realtor Services', '/realtor-services'],
];
const CITY_PATHS = { Albuquerque: '/', 'Rio Rancho': '/rio-rancho', 'Santa Fe': '/santa-fe', Corrales: '/corrales', Bernalillo: '/bernalillo', 'Los Lunas': '/los-lunas', Placitas: '/placitas' };

const AboutPage = () => {
  const navigate = useNavigate();
  const jsonLd = [
    organizationJsonLd(),
    { '@context': 'https://schema.org', ...localBusinessNode(), description: BUSINESS.description },
    breadcrumbJsonLd([{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }]),
  ];
  const blocks = [
    { icon: HomeIcon, title: 'What we do', text: ABOUT_FACTS.what },
    { icon: Building2, title: 'Who we serve', text: ABOUT_FACTS.who },
    { icon: MapPin, title: 'Where we work', text: ABOUT_FACTS.where },
    { icon: ClipboardList, title: 'How to request an estimate', text: ABOUT_FACTS.how },
    { icon: ShieldCheck, title: 'Insurance documentation', text: ABOUT_FACTS.insurance },
  ];
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Seo
        title="About Us | A Quality Handyman LLC, Albuquerque NM"
        description="A Quality Handyman LLC is a locally owned, insured handyman and home-improvement company serving Albuquerque, Rio Rancho, Corrales, Bernalillo, Placitas, Los Lunas and Santa Fe, NM. Residential & commercial repairs, painting, stucco, remodeling and maintenance. Call (505) 219-7816."
        path="/about"
        jsonLd={jsonLd}
      />
      <section className="bg-gradient-to-r from-brand-blue to-brand-teal text-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm uppercase tracking-wide text-white/80 mb-2">About us</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">A Quality Handyman LLC — Albuquerque's Local Repair &amp; Maintenance Team</h1>
          <p className="text-lg text-white/90 max-w-3xl">Locally owned. Residential and commercial. Serving Albuquerque, New Mexico and surrounding communities.</p>
          <div className="flex flex-wrap gap-3 mt-6">
            <Button size="lg" onClick={() => navigate('/estimate')} className="bg-white text-brand-blue hover:bg-white/90" data-testid="about-cta-estimate"><ClipboardList className="w-5 h-5 mr-2" /> Get an Instant Estimate</Button>
            <Button size="lg" variant="outline" asChild className="border-white text-white hover:bg-white/10"><a href="tel:+15052197816" data-testid="about-cta-call"><Phone className="w-5 h-5 mr-2" /> (505) 219-7816</a></Button>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          {blocks.map((b) => (
            <Card key={b.title} className="border-slate-200"><CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3"><div className="w-10 h-10 rounded-full bg-brand-teal/10 flex items-center justify-center"><b.icon className="w-5 h-5 text-brand-teal" /></div><h2 className="text-lg font-bold text-slate-900">{b.title}</h2></div>
              <p className="text-slate-600 leading-relaxed">{b.text}</p>
            </CardContent></Card>
          ))}
        </div>
      </section>

      <section className="py-12 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Services we provide</h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="about-services-list">
            {SERVICE_LINKS.map(([n, p]) => <li key={p}><Link to={p} className="text-brand-blue hover:text-brand-teal underline underline-offset-2">{n}</Link></li>)}
          </ul>
          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">Communities we serve</h2>
          <ul className="flex flex-wrap gap-2" data-testid="about-areas-list">
            {SERVICE_AREAS.map((c) => <li key={c}><Link to={CITY_PATHS[c]} className="inline-block rounded-full border border-slate-300 px-3 py-1 text-sm text-slate-700 hover:border-brand-teal hover:text-brand-teal">{c}, NM</Link></li>)}
          </ul>
          <p className="text-slate-600 mt-8 text-sm"><Camera className="inline w-4 h-4 mr-1 text-brand-teal" />See real completed work in our <Link to="/gallery" className="text-brand-blue underline">project gallery</Link> and <Link to="/project/exterior-stucco" className="text-brand-blue underline">project pages</Link>.</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Contact</h2>
          <address className="not-italic text-slate-700 space-y-1" data-testid="about-contact">
            <p><strong>A Quality Handyman LLC</strong> — service-area business, Albuquerque, New Mexico (no public walk-in location)</p>
            <p>Phone / text: <a href="tel:+15052197816" className="text-brand-blue underline">(505) 219-7816</a></p>
            <p>Email: <a href="mailto:admin@aqualityhandyman.com" className="text-brand-blue underline">admin@aqualityhandyman.com</a></p>
            <p>Website: <a href="https://aqualityhandyman.com" className="text-brand-blue underline">https://aqualityhandyman.com</a></p>
          </address>
          <div className="flex flex-wrap gap-3 mt-6">
            <Button onClick={() => navigate('/booking')} className="bg-brand-teal hover:bg-brand-teal/90 text-white" data-testid="about-cta-book">Book an On-Site Estimate</Button>
            <Button variant="outline" onClick={() => navigate('/#contact')} data-testid="about-cta-contact">Send a Message</Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
